import { Button } from "@jugl-web/ui-kit";
import { PlainButton } from "@jugl-web/ui-components";
import { cx, useToast, useTranslations } from "@jugl-web/utils";
import { useShowAlert } from "@jugl-web/domain-resources/ui-components";
import { FC, ReactNode, useCallback, useMemo, useRef } from "react";
import { PlanCardInfo } from "@web-src/modules/cx/components/PlanCardInfo";
import { useRestApiProvider, getPendingInvoiceType } from "@jugl-web/rest-api";
import useEntity from "@web-src/features/app/hooks/useEntity";
import { format } from "date-fns";
import { usePlanFeatures } from "@web-src/modules/cx/components/WorkspaceSubscriptionDrawer/hooks/usePlansData";
import { useSubscriptionV2 } from "../../providers/SubscriptionV2Provider";
import { getNextTierPlan } from "./utils/planTierUtils";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { UpgradeToProCard } from "./components/UpgradeToProCard";
import { SaveUpToCard } from "./components/SaveUpToCard/SaveUpToCard";
import { AddonCard } from "./components/AddonCard";
import { ADDONS, HIDDEN_ADDON_IDS } from "./components/AddonCard/conts";
import { SelectedAddon } from "./components/AddonCard/types";
import { ManageSubscriptionCard } from "./components/ManageSubscriptionCard/ManageSubscriptionCard";
import { CancelSubscriptionCard } from "./components/CancelSubscriptionCard";
import {
  GetExtraCreditsCard,
  GetExtraCreditsCardHandle,
} from "./components/GetExtraCreditsCard/GetExtraCreditsCard";
import {
  AutoRechargeCard,
  AutoRechargeCardHandle,
} from "./components/AutoRechargeCard/AutoRechargeCard";

export const CXPlansPage = () => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const showAlert = useShowAlert();
  const { entity } = useEntity();

  const extraCreditsRef = useRef<GetExtraCreditsCardHandle>(null);
  const autoRechargeRef = useRef<AutoRechargeCardHandle>(null);

  const {
    entitySubscription,
    plans,
    supportedAddons: addonsData,
    currentPlan: planInfo,
    extraUserCount,
    isFree,
    isMonthly,
    pendingInvoices,
    hasPaymentFailed,
    isSubscriptionCanceled,
    subscription,
    refetchSubscription,
    cancelSubscription,
    isSubscriptionPendingCancel,
    openSubscriptionDrawer,
    openPurchaseDialog,
    adjustSeats,
    isChangePlanInProgress,
    reactivateCurrentPlan,
  } = useSubscriptionV2();

  const { subscriptionV2Api } = useRestApiProvider();

  const [createPortalSession] =
    subscriptionV2Api.useCreatePortalSessionMutation();

  const { data: topupConfig, refetch: refetchTopupConfig } =
    subscriptionV2Api.useGetTopupConfigQuery(
      { entityId: entity?.id ?? "" },
      { skip: !entity?.id }
    );

  // Build SelectedAddon map for AddonCards
  const addonSelections = useMemo(() => {
    const result: Record<string, SelectedAddon> = {};
    if (!entitySubscription) return result;

    ADDONS.forEach((addon) => {
      if (addon.id === "jugl::users") {
        if (extraUserCount > 0) {
          result[addon.id] = {
            type: "quantity",
            quantity: extraUserCount,
            addon,
          };
        }
        return;
      }
      const current = entitySubscription.addonLineItems[addon.id];
      if (current && current.quantity > 0) {
        result[addon.id] = {
          type: "quantity",
          quantity: current.quantity,
          addon,
        };
      }
    });

    return result;
  }, [entitySubscription, extraUserCount]);

  // Enrich addon display data with real API prices
  const enrichedAddons = useMemo(() => {
    return ADDONS.map((addon) => {
      const apiAddon = addonsData?.find((a) => a.id === addon.id);
      if (!apiAddon) return addon;

      const matchingPrice = apiAddon.prices.find(
        (p) => p.interval === (isMonthly ? "month" : "year")
      );

      return {
        ...addon,
        price: matchingPrice ? matchingPrice.unit_amount / 100 : addon.price,
      };
    });
  }, [addonsData, isMonthly]);

  const currentPlanFeatures = usePlanFeatures({
    userCredits:
      subscription?.entitlements?.["jugl::users"] ??
      planInfo?.default_addons.find((a) => a.id === "jugl::users")?.credits ??
      1,
    aiCredits:
      subscription?.entitlements?.["jugl::ai_credits"] ??
      planInfo?.default_addons.find((a) => a.id === "jugl::ai_credits")
        ?.credits ??
      0,
    knowledgeCredits:
      subscription?.entitlements?.["jugl::knowledge"] ??
      planInfo?.default_addons.find((a) => a.id === "jugl::knowledge")
        ?.credits ??
      0,
  });

  // Get the next higher-tier plan for the upgrade card (skip free plan idx=0)
  const nextTierPlan = useMemo(() => {
    if (!plans) return null;
    if (!planInfo) {
      // No current plan — pick first non-free plan
      return (
        [...plans]
          .sort((a, b) => a.meta_data.idx - b.meta_data.idx)
          .find((p) => p.meta_data.idx > 0) || null
      );
    }
    return getNextTierPlan(planInfo, plans) ?? null;
  }, [planInfo, plans]);

  // Show "Boost Your Plan" section only when there's something to show
  const showUpgradeCard = !!nextTierPlan;
  const showGoYearlyCard = !!isMonthly && !isFree;
  const planPendingInvoice = pendingInvoices?.find(
    (i) => getPendingInvoiceType(i) === "plan"
  );
  const addonPendingInvoice = pendingInvoices?.find((i) => {
    const invoiceType = getPendingInvoiceType(i);
    return invoiceType === "addon-seats" || invoiceType === "addon-credits";
  });
  const manualRechargePendingInvoice = pendingInvoices?.find(
    (i) => getPendingInvoiceType(i) === "recharge-manual"
  );
  const autoRechargePendingInvoice = pendingInvoices?.find(
    (i) => getPendingInvoiceType(i) === "recharge-auto"
  );

  const showBoostSection = showUpgradeCard || showGoYearlyCard;

  // Handler for switching to annual billing
  const handleGoYearly = useCallback(() => {
    showAlert({
      title: t({
        id: "cx-plans-page.go-yearly-alert-title",
        defaultMessage: "Save money with better deal",
      }),
      content: (() => {
        const yrPrice = planInfo?.prices.find((p) => p.interval === "year");
        const monthlyPrice = planInfo?.prices.find(
          (p) => p.interval === "month"
        );
        const yrAmount = yrPrice ? yrPrice.unit_amount / 100 : 0;
        const monthlyTotal = monthlyPrice
          ? (monthlyPrice.unit_amount / 100) * 12
          : 0;
        const savings = monthlyTotal - yrAmount;
        const savingsPercent =
          monthlyTotal > 0 ? Math.round((savings / monthlyTotal) * 100) : 18;

        return (
          <div className="text-dark-700 text-center">
            {t(
              {
                id: "cx-plans-page.go-yearly-alert-content",
                defaultMessage:
                  "You're about to upgrade your Plan to Annual billing for <highlight>{yearlyPrice} per year</highlight>. You will <highlight>save {savingsPercent}% ({savingsAmount})</highlight> \ud83d\ude0d. Today you will be charged remaining amount for the new billing cycle",
              },
              {
                yearlyPrice: `$${yrAmount}`,
                savingsPercent,
                savingsAmount: `$${savings}`,
                highlight: (chunks: ReactNode) => (
                  <span className="bg-gradient-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                    {chunks}
                  </span>
                ),
              }
            )}
          </div>
        );
      })(),
      buttons: [
        {
          text: t({
            id: "cx-plans-page.go-yearly-alert-cancel",
            defaultMessage: "Cancel",
          }),
          color: "grey",
          role: "close",
        },
        {
          text: t({
            id: "cx-plans-page.go-yearly-alert-confirm",
            defaultMessage: "Upgrade",
          }),
          color: "primary",
          onClick: (_, { closeAlert }) => {
            closeAlert();
            if (!planInfo) return;
            const yearlyPrice = planInfo.prices.find(
              (p) => p.interval === "year"
            );
            if (!yearlyPrice) return;
            openPurchaseDialog({ planId: planInfo.id, period: "year" });
          },
        },
      ],
    });
  }, [showAlert, planInfo, openPurchaseDialog, t]);

  // Handler for upgrading to next tier plan
  const handleUpgradePlan = useCallback(() => {
    if (!nextTierPlan) return;

    const matchingPrice = nextTierPlan.prices.find(
      (p) => p.interval === (isMonthly ? "month" : "year")
    );
    if (!matchingPrice) return;

    showAlert({
      title: t({
        id: "cx-plans-page.upgrade-plan-alert-title",
        defaultMessage: "Upgrade Plan",
      }),
      content: (
        <div className="text-dark-700 text-center">
          {t(
            {
              id: "cx-plans-page.upgrade-plan-alert-content",
              defaultMessage:
                "You're about to upgrade your Plan to <highlight>{planName}</highlight> for <highlight>{price} per {interval}</highlight>. Today you will be charged remaining amount for the new billing cycle",
            },
            {
              planName: nextTierPlan.name,
              price: `$${matchingPrice.unit_amount / 100}`,
              interval: isMonthly ? "month" : "year",
              highlight: (chunks: ReactNode) => (
                <span className="bg-gradient-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                  {chunks}
                </span>
              ),
            }
          )}
        </div>
      ),
      buttons: [
        {
          text: t({
            id: "cx-plans-page.upgrade-plan-alert-cancel",
            defaultMessage: "Cancel",
          }),
          color: "grey",
          role: "close",
        },
        {
          text: t({
            id: "cx-plans-page.upgrade-plan-alert-confirm",
            defaultMessage: "Upgrade",
          }),
          color: "primary",
          onClick: (_, { closeAlert }) => {
            closeAlert();
            openPurchaseDialog({
              planId: nextTierPlan.id,
              period: isMonthly ? "month" : "year",
            });
          },
        },
      ],
    });
  }, [showAlert, nextTierPlan, isMonthly, openPurchaseDialog, t]);

  const handleManageSubscription = useCallback(async () => {
    if (!entity) return;
    try {
      const response = await createPortalSession({
        entityId: entity.id,
        data: { return_url: window.location.href },
      }).unwrap();
      window.open(response.url, "_blank");

      // Refetch subscription when user returns from portal
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          refetchSubscription();
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange
          );
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
    } catch {
      toast(
        t({
          id: "cx-plans-page.manage-subscription-failed",
          defaultMessage: "Failed to open subscription management",
        }),
        { variant: "error" }
      );
    }
  }, [entity, createPortalSession, refetchSubscription, toast, t]);

  return (
    <div className="mx-auto max-w-[1032px] space-y-20 px-4 pt-6 pb-[100px]">
      <div>
        <PlansPageHeading
          title={t({
            id: "cx-plans-page.current-plan-title",
            defaultMessage: "Current Plan",
          })}
        />
        <PlanCardInfo
          planName={entitySubscription?.planName || planInfo?.name || "Free"}
          billingLabel={isFree ? "" : isMonthly ? "Monthly" : "Yearly"}
          renewalLabel={
            entitySubscription?.currentPeriodEnd &&
            !hasPaymentFailed &&
            !isSubscriptionPendingCancel &&
            !isSubscriptionCanceled
              ? `Renews on ${format(
                  new Date(entitySubscription.currentPeriodEnd),
                  "MMMM dd, yyyy"
                )}`
              : ""
          }
          price={entitySubscription?.totalPrice || 0}
          pricePeriodLabel={isMonthly ? "per Month" : "per Year"}
          features={currentPlanFeatures}
          isChangeHidden
          hasPaymentFailed={hasPaymentFailed}
          paymentFailedDescription={
            planPendingInvoice ? (
              <>
                Something went wrong during the checkout. Please{" "}
                <PlainButton
                  className="inline text-sm text-[#F2F2F4] underline"
                  onClick={() => {
                    if (planPendingInvoice.invoice_url) {
                      window.open(planPendingInvoice.invoice_url, "_blank");
                    }
                  }}
                >
                  update your payment details
                </PlainButton>{" "}
                to continue using {planPendingInvoice.description}. We will
                auto-retry payment tomorrow
              </>
            ) : undefined
          }
          isSubscriptionCanceled={isSubscriptionCanceled}
          isSubscriptionPendingCancel={isSubscriptionPendingCancel}
          cancelAt={subscription?.cancel_at}
          onTryAgain={
            planPendingInvoice
              ? () => window.open(planPendingInvoice.invoice_url, "_blank")
              : undefined
          }
          onUpdatePaymentDetails={handleManageSubscription}
          onRenewSubscription={
            isSubscriptionPendingCancel
              ? reactivateCurrentPlan
              : () => openSubscriptionDrawer()
          }
        />
      </div>

      {(!isSubscriptionCanceled || isSubscriptionPendingCancel) && (
        <>
          <div>
            <PlansPageHeading
              title={t({
                id: "cx-plans-page.boost-your-plan-title",
                defaultMessage: "Boost Your Plan",
              })}
              rightContent={
                <Button
                  variant="tertiary"
                  iconEnd={<ArrowRightIcon />}
                  onClick={() => openSubscriptionDrawer()}
                >
                  {t({
                    id: "cx-plans-page.see-all-plans-button",
                    defaultMessage: "See All Plans",
                  })}
                </Button>
              }
            />
            {showBoostSection && (
              <div className="grid grid-cols-9 gap-6">
                {showUpgradeCard && (
                  <div
                    className={showGoYearlyCard ? "col-span-5" : "col-span-9"}
                  >
                    <UpgradeToProCard
                      nextPlanName={nextTierPlan?.name ?? "Pro"}
                      onClick={handleUpgradePlan}
                    />
                  </div>
                )}
                {showGoYearlyCard && (
                  <div
                    className={showUpgradeCard ? "col-span-4" : "col-span-9"}
                  >
                    <SaveUpToCard
                      onGoYearly={handleGoYearly}
                      isLoading={isChangePlanInProgress}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <PlansPageHeading
              title={t({
                id: "cx-plans-page.add-ons-title",
                defaultMessage: "Add-ons",
              })}
            />
            <div className="flex flex-col gap-8">
              {enrichedAddons
                .filter((addon) => !HIDDEN_ADDON_IDS.includes(addon.id))
                .map((addon) => (
                  <AddonCard
                    key={addon.id}
                    addon={addon}
                    selected={addonSelections[addon.id]}
                    disabled={isFree}
                    interactionDisabled={!!addonPendingInvoice}
                    pendingInvoice={addonPendingInvoice}
                    onClick={() => {
                      if (isFree || addonPendingInvoice) return;
                      if (addon.id === "jugl::users") {
                        adjustSeats();
                      }
                    }}
                  />
                ))}
              <GetExtraCreditsCard
                ref={extraCreditsRef}
                pendingInvoice={manualRechargePendingInvoice}
                disabled={!!manualRechargePendingInvoice}
                onRequestSwitch={() => autoRechargeRef.current?.open()}
              />
              <AutoRechargeCard
                ref={autoRechargeRef}
                topupConfig={topupConfig}
                onConfigUpdated={refetchTopupConfig}
                pendingInvoice={autoRechargePendingInvoice}
                onRequestSwitch={() => extraCreditsRef.current?.open()}
              />
            </div>
          </div>
          <div>
            <PlansPageHeading
              title={t({
                id: "cx-plans-page.settings-title",
                defaultMessage: "Settings",
              })}
            />
            <div className="flex flex-col gap-8">
              <ManageSubscriptionCard onManage={handleManageSubscription} />
              {!isFree && !isSubscriptionPendingCancel && (
                <CancelSubscriptionCard onCancel={cancelSubscription} />
              )}
            </div>
          </div>
        </>
      )}
      {isSubscriptionCanceled && !isSubscriptionPendingCancel && (
        <>
          <div>
            <PlansPageHeading
              title={t({
                id: "cx-plans-page.select-plan-title",
                defaultMessage: "Select Plan",
              })}
              rightContent={
                <Button
                  variant="tertiary"
                  iconEnd={<ArrowRightIcon />}
                  onClick={() => openSubscriptionDrawer()}
                >
                  {t({
                    id: "cx-plans-page.see-all-plans-button",
                    defaultMessage: "See All Plans",
                  })}
                </Button>
              }
            />
            <div className="grid grid-cols-2 gap-6">
              <UpgradeToProCard
                nextPlanName="Pro Plan"
                description="Unlock deeper insights and higher AI Credits. For medium team for smart business management"
                buttonLabel="Get started"
                onClick={() => openSubscriptionDrawer()}
              />
              <UpgradeToProCard
                nextPlanName="Business Plan"
                description="Get advanced analytics, unlimited tickets and higher AI Credits. For larger teams for powerful usage"
                buttonLabel="Get started"
                variant="alt"
                onClick={() => openSubscriptionDrawer()}
              />
            </div>
          </div>
          <div>
            <PlansPageHeading
              title={t({
                id: "cx-plans-page.settings-title",
                defaultMessage: "Settings",
              })}
            />
            <ManageSubscriptionCard onManage={handleManageSubscription} />
          </div>
        </>
      )}
    </div>
  );
};

const PlansPageHeading: FC<{
  title: string;
  rightContent?: ReactNode;
  className?: string;
}> = ({ title, rightContent, className }) => {
  return (
    <div
      className={cx("mb-4 flex items-center justify-between px-2", className)}
    >
      <h2 className="text-dark my-0 text-xl font-medium">{title}</h2>
      {rightContent}
    </div>
  );
};
